import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  BarChart3,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Users,
  Briefcase,
  Eye,
  Clock,
  Target,
  Award,
  Calendar,
  Download,
  RefreshCw,
  ChevronDown,
  Zap,
  MapPin,
  Building2
} from "lucide-react";

const AdvancedAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30d");
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const endpoint = user?.role === "recruiter" 
        ? `/analytics/recruiter/advanced?range=${timeRange}`
        : `/analytics/jobseeker/advanced?range=${timeRange}`;
      
      const response = await fetch(`${API}${endpoint}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        console.error("Analytics API error:", response.status);
        toast.error("Failed to load analytics");
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const getMockData = () => {
    if (user?.role === "recruiter") {
      return {
        overview: {
          total_jobs: 12,
          active_jobs: 8,
          total_applicants: 245,
          interviews_scheduled: 34,
          offers_sent: 8,
          hires: 5
        },
        trends: {
          applicants_change: 23,
          views_change: 15,
          conversion_rate: 3.2
        },
        top_performing_jobs: [
          { title: "Senior React Developer", applicants: 45, views: 1200, conversion: 3.75 },
          { title: "Product Manager", applicants: 38, views: 980, conversion: 3.88 },
          { title: "Data Scientist", applicants: 32, views: 750, conversion: 4.27 }
        ],
        applicant_sources: [
          { source: "Direct", count: 120, percentage: 49 },
          { source: "LinkedIn", count: 65, percentage: 27 },
          { source: "Referral", count: 35, percentage: 14 },
          { source: "Other", count: 25, percentage: 10 }
        ],
        hiring_funnel: {
          applied: 245,
          screened: 180,
          interviewed: 85,
          offered: 25,
          hired: 12
        },
        time_to_hire: {
          average_days: 28,
          fastest: 14,
          slowest: 45
        },
        top_skills_demand: [
          { skill: "React", count: 89 },
          { skill: "Python", count: 76 },
          { skill: "AWS", count: 65 },
          { skill: "Node.js", count: 58 },
          { skill: "TypeScript", count: 52 }
        ]
      };
    } else {
      return {
        overview: {
          profile_views: 156,
          applications_sent: 24,
          interviews: 8,
          offers: 2,
          profile_completion: 85
        },
        trends: {
          views_change: 18,
          response_rate: 42,
          avg_match_score: 72
        },
        application_status: [
          { status: "Under Review", count: 12 },
          { status: "Interviewed", count: 6 },
          { status: "Offered", count: 2 },
          { status: "Rejected", count: 4 }
        ],
        skills_analysis: {
          top_matched: ["React", "JavaScript", "Node.js", "MongoDB"],
          missing: ["AWS", "Docker", "Kubernetes"],
          trending: ["AI/ML", "TypeScript", "GraphQL"]
        },
        job_match_distribution: [
          { range: "90-100%", count: 5 },
          { range: "70-89%", count: 15 },
          { range: "50-69%", count: 25 },
          { range: "Below 50%", count: 10 }
        ],
        activity_timeline: [
          { date: "This Week", applications: 8, views: 45 },
          { date: "Last Week", applications: 6, views: 38 },
          { date: "2 Weeks Ago", applications: 5, views: 32 },
          { date: "3 Weeks Ago", applications: 5, views: 41 }
        ]
      };
    }
  };

  const timeRanges = [
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "1y", label: "Last year" }
  ];

  const dashboardUrl = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  const exportData = () => {
    const dataStr = JSON.stringify(analytics, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rekrutor-analytics-${timeRange}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Analytics exported!");
  };

  const exportPDF = async () => {
    try {
      toast.loading("Generating PDF report...", { id: "pdf-export" });
      const reportType = user?.role === "recruiter" ? "recruiter" : "jobseeker";
      const response = await fetch(`${API}/reports/analytics/pdf?report_type=${reportType}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `rekrutor-${reportType}-analytics.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success("PDF report downloaded!", { id: "pdf-export" });
      } else {
        toast.error("Failed to generate PDF", { id: "pdf-export" });
      }
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to generate PDF", { id: "pdf-export" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isRecruiter = user?.role === "recruiter";

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                ADVANCED <span className="text-crimson">ANALYTICS</span>
              </h1>
              <p className="text-gray-400 text-sm sm:text-base">Track your performance metrics</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Time Range Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                  className="flex items-center gap-2 px-3 py-2 bg-obsidian-light border border-[#333] text-sm text-white"
                  data-testid="time-range-dropdown"
                >
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">{timeRanges.find(t => t.value === timeRange)?.label}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showTimeDropdown && (
                  <div className="absolute right-0 mt-1 w-40 bg-obsidian-light border border-[#333] shadow-lg z-10">
                    {timeRanges.map((range) => (
                      <button
                        key={range.value}
                        onClick={() => {
                          setTimeRange(range.value);
                          setShowTimeDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-white/5 ${
                          timeRange === range.value ? "text-crimson" : "text-gray-300"
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button
                onClick={exportPDF}
                variant="outline"
                className="border-crimson text-crimson hover:bg-crimson hover:text-white"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">PDF</span>
              </Button>
              <Button
                onClick={exportData}
                variant="outline"
                className="border-[#333] text-white"
                data-testid="export-json-btn"
              >
                <Download className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">JSON</span>
              </Button>
            </div>
          </div>

          {isRecruiter ? (
            <RecruiterAnalyticsContent analytics={analytics} />
          ) : (
            <JobSeekerAnalyticsContent analytics={analytics} />
          )}
        </div>
      </main>
    </div>
  );
};

const RecruiterAnalyticsContent = ({ analytics }) => (
  <div className="space-y-6">
    {/* Overview Cards */}
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {[
        { label: "Total Jobs", value: analytics?.overview?.total_jobs, icon: Briefcase },
        { label: "Active Jobs", value: analytics?.overview?.active_jobs, icon: Target },
        { label: "Applicants", value: analytics?.overview?.total_applicants, icon: Users },
        { label: "Interviews", value: analytics?.overview?.interviews_scheduled, icon: Calendar },
        { label: "Offers Sent", value: analytics?.overview?.offers_sent, icon: Award },
        { label: "Hires", value: analytics?.overview?.hires, icon: Users },
      ].map((stat, i) => (
        <motion.div
          key={stat.label}
          className="bg-obsidian-light border border-[#222] p-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}
        >
          <div className="flex items-center gap-2 text-gray-400 mb-2">
            <stat.icon className="w-4 h-4" />
            <span className="text-xs">{stat.label}</span>
          </div>
          <div className="text-2xl font-bold text-white">{stat.value || 0}</div>
        </motion.div>
      ))}
    </div>

    {/* Trends */}
    <motion.div
      className="bg-obsidian-light border border-[#222] p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <h3 className="font-heading text-lg font-bold mb-4">Trends</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 flex items-center justify-center ${analytics?.trends?.applicants_change > 0 ? "bg-green-500/20" : "bg-red-500/20"}`}>
            {analytics?.trends?.applicants_change > 0 ? (
              <TrendingUp className="w-6 h-6 text-green-500" />
            ) : (
              <TrendingDown className="w-6 h-6 text-red-500" />
            )}
          </div>
          <div>
            <div className="text-2xl font-bold">{Math.abs(analytics?.trends?.applicants_change || 0)}%</div>
            <div className="text-gray-400 text-sm">Applicant Change</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 flex items-center justify-center ${analytics?.trends?.views_change > 0 ? "bg-green-500/20" : "bg-red-500/20"}`}>
            <Eye className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <div className="text-2xl font-bold">{analytics?.trends?.views_change || 0}%</div>
            <div className="text-gray-400 text-sm">Views Change</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-crimson/20 flex items-center justify-center">
            <Target className="w-6 h-6 text-crimson" />
          </div>
          <div>
            <div className="text-2xl font-bold">{analytics?.trends?.conversion_rate || 0}%</div>
            <div className="text-gray-400 text-sm">Conversion Rate</div>
          </div>
        </div>
      </div>
    </motion.div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Top Performing Jobs */}
      <motion.div
        className="bg-obsidian-light border border-[#222] p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h3 className="font-heading text-lg font-bold mb-4">Top Performing Jobs</h3>
        <div className="space-y-4">
          {analytics?.top_performing_jobs?.map((job, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-obsidian border border-[#333]">
              <div>
                <div className="font-medium text-white">{job.title}</div>
                <div className="text-sm text-gray-400">{job.applicants} applicants • {job.views} views</div>
              </div>
              <div className="text-crimson font-bold">{job.conversion}%</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Hiring Funnel */}
      <motion.div
        className="bg-obsidian-light border border-[#222] p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h3 className="font-heading text-lg font-bold mb-4">Hiring Funnel</h3>
        <div className="space-y-3">
          {[
            { label: "Applied", value: analytics?.hiring_funnel?.applied, color: "bg-blue-500" },
            { label: "Screened", value: analytics?.hiring_funnel?.screened, color: "bg-cyan-500" },
            { label: "Interviewed", value: analytics?.hiring_funnel?.interviewed, color: "bg-yellow-500" },
            { label: "Offered", value: analytics?.hiring_funnel?.offered, color: "bg-orange-500" },
            { label: "Hired", value: analytics?.hiring_funnel?.hired, color: "bg-green-500" },
          ].map((stage) => {
            const percentage = analytics?.hiring_funnel?.applied ? (stage.value / analytics.hiring_funnel.applied * 100) : 0;
            return (
              <div key={stage.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">{stage.label}</span>
                  <span className="text-white">{stage.value}</span>
                </div>
                <div className="h-2 bg-obsidian rounded-full overflow-hidden">
                  <div className={`h-full ${stage.color}`} style={{ width: `${percentage}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>

    {/* Top Skills in Demand */}
    <motion.div
      className="bg-obsidian-light border border-[#222] p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
    >
      <h3 className="font-heading text-lg font-bold mb-4">Top Skills in Demand</h3>
      <div className="flex flex-wrap gap-3">
        {analytics?.top_skills_demand?.map((skill, i) => (
          <div
            key={skill.skill}
            className="px-4 py-2 bg-obsidian border border-[#333] flex items-center gap-2"
          >
            <span className="text-white">{skill.skill}</span>
            <span className="text-crimson font-bold">{skill.count}</span>
          </div>
        ))}
      </div>
    </motion.div>
  </div>
);

const JobSeekerAnalyticsContent = ({ analytics }) => {
  const navigate = useNavigate();
  
  const cards = [
    { label: "Profile Views", value: analytics?.overview?.profile_views, icon: Eye, tab: null },
    { label: "Applications", value: analytics?.overview?.applications_sent, icon: Briefcase, tab: "applications" },
    { label: "Interviews", value: analytics?.overview?.interviews, icon: Calendar, tab: "interviews" },
    { label: "Offers", value: analytics?.overview?.offers, icon: Award, tab: "offers" },
    { label: "Profile", value: `${analytics?.overview?.profile_completion || 0}%`, icon: Users, tab: null, link: "/profile/build" },
  ];

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((stat, i) => (
          <motion.div
            key={stat.label}
            className={`bg-obsidian-light border border-[#222] p-4 ${
              stat.tab || stat.link ? "cursor-pointer hover:border-crimson/30 transition-colors" : ""
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            onClick={() => {
              if (stat.link) {
                navigate(stat.link);
              } else if (stat.tab) {
                navigate(`/my-applications?tab=${stat.tab}`);
              }
            }}
          >
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <stat.icon className="w-4 h-4" />
              <span className="text-xs">{stat.label}</span>
            </div>
            <div className="text-2xl font-bold text-white">{stat.value || 0}</div>
          </motion.div>
        ))}
      </div>

    {/* Trends */}
    <motion.div
      className="bg-obsidian-light border border-[#222] p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <h3 className="font-heading text-lg font-bold mb-4">Your Performance</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-green-500/20 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-green-500" />
          </div>
          <div>
            <div className="text-2xl font-bold">{analytics?.trends?.views_change || 0}%</div>
            <div className="text-gray-400 text-sm">Profile Views Up</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-cyan-500/20 flex items-center justify-center">
            <Target className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <div className="text-2xl font-bold">{analytics?.trends?.response_rate || 0}%</div>
            <div className="text-gray-400 text-sm">Response Rate</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-crimson/20 flex items-center justify-center">
            <Award className="w-6 h-6 text-crimson" />
          </div>
          <div>
            <div className="text-2xl font-bold">{analytics?.trends?.avg_match_score || 0}%</div>
            <div className="text-gray-400 text-sm">Avg Match Score</div>
          </div>
        </div>
      </div>
    </motion.div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Application Status */}
      <motion.div
        className="bg-obsidian-light border border-[#222] p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h3 className="font-heading text-lg font-bold mb-4">Application Status</h3>
        <div className="space-y-3">
          {analytics?.application_status?.map((status, i) => {
            const total = analytics?.application_status?.reduce((sum, s) => sum + s.count, 0) || 1;
            const percentage = (status.count / total * 100);
            const colors = {
              "Under Review": "bg-blue-500",
              "Interviewed": "bg-yellow-500",
              "Offered": "bg-green-500",
              "Rejected": "bg-red-500"
            };
            return (
              <div key={status.status}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">{status.status}</span>
                  <span className="text-white">{status.count}</span>
                </div>
                <div className="h-2 bg-obsidian rounded-full overflow-hidden">
                  <div className={`h-full ${colors[status.status] || "bg-gray-500"}`} style={{ width: `${percentage}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Skills Analysis */}
      <motion.div
        className="bg-obsidian-light border border-[#222] p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h3 className="font-heading text-lg font-bold mb-4">Skills Analysis</h3>
        <div className="space-y-4">
          <div>
            <div className="text-sm text-gray-400 mb-2">Top Matched Skills</div>
            <div className="flex flex-wrap gap-2">
              {analytics?.skills_analysis?.top_matched?.map((skill) => (
                <span key={skill} className="px-3 py-1 bg-green-500/20 text-green-400 text-sm">{skill}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-400 mb-2">Skills to Add</div>
            <div className="flex flex-wrap gap-2">
              {analytics?.skills_analysis?.missing?.map((skill) => (
                <span key={skill} className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-sm">{skill}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-400 mb-2">Trending Skills</div>
            <div className="flex flex-wrap gap-2">
              {analytics?.skills_analysis?.trending?.map((skill) => (
                <span key={skill} className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-sm">{skill}</span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>

    {/* Job Match Distribution */}
    <motion.div
      className="bg-obsidian-light border border-[#222] p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
    >
      <h3 className="font-heading text-lg font-bold mb-4">Job Match Distribution</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {analytics?.job_match_distribution?.map((dist) => (
          <div key={dist.range} className="text-center p-4 bg-obsidian border border-[#333]">
            <div className="text-3xl font-bold text-crimson mb-1">{dist.count}</div>
            <div className="text-sm text-gray-400">{dist.range}</div>
          </div>
        ))}
      </div>
    </motion.div>
  </div>
  );
};

export default AdvancedAnalytics;
